import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Modal, ModalActions, ModalContent, ModalTitleInverse, useSnackbar } from '@c2fo/liquidity';
import Skeleton from '@/enterprise/components/Skeleton';
import useSubmitBenchmarkOffer from '@/enterprise/data/useSubmitBenchmarkOffer';
import useSubmitPriceDiscoveryOffer from '@/enterprise/data/useSubmitPriceDiscoveryOffer';
import useSubmitStaticOffer from '@/enterprise/data/useSubmitStaticOffer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { SchedulingInput } from '@/enterprise/features/offerModal/inputs/SchedulingInput';
import { Cadence, ScheduleInput } from '@/enterprise/features/offerModal/types';
import {
  buildCadenceFromExistingOfferSchedule,
  buildCustomScheduleSentenceFromFormData,
  checkIfDurationIsATypicalOngoingOffer,
} from '@/enterprise/features/offerModal/utils';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import getTakerMarketName from '@/utils/getTakerMarketName';
import { trimDateTimestamp } from '@/utils/trimDateTimestamp';

interface EditScheduleModalProps {
  open: boolean;
  takerMarket: TakerMarket;
  onClose: () => void;
}

const EditScheduleModalSkeleton = () => {
  return (
    <ModalContent className="pt-2">
      <div className="space-y-4">
        <Skeleton className="h-14 w-3/5" shadow={false} />
        <div className="space-y-4">
          <Skeleton className="h-20 w-full rounded-xl" shadow={false} />
          <Skeleton className="h-20 w-full rounded-xl" shadow={false} />
          <Skeleton className="h-20 w-full rounded-t-xl" shadow={false} />
        </div>
      </div>
    </ModalContent>
  );
};

const getScheduleFromTakerMarket = (takerMarket: TakerMarket) => {
  const cadence = buildCadenceFromExistingOfferSchedule(takerMarket.offerSchedule?.schedule);
  const startDate = takerMarket.offerSchedule?.startDate ?? undefined;
  let endDate = takerMarket.offerSchedule?.endDate ?? undefined;

  // Use disableAfterMarketClearsDate as fallback for end date
  if (!endDate && takerMarket.disableAfterMarketClearsDate) {
    endDate = trimDateTimestamp(takerMarket.disableAfterMarketClearsDate);
  }

  return { cadence, startDate, endDate };
};

const EditScheduleModalContent = ({ open, takerMarket, onClose }: EditScheduleModalProps) => {
  const { t } = useTranslation();
  const { addError } = useDataDog();
  const showSnackbar = useSnackbar();
  const { trackEnterpriseEvent } = useReporting();
  const { mutateAsync: submitPriceDiscoveryOffer, isPending: isPendingPriceDiscovery } = useSubmitPriceDiscoveryOffer();
  const { mutateAsync: submitBenchmarkOffer, isPending: isPendingBenchmark } = useSubmitBenchmarkOffer();
  const { mutateAsync: submitStaticOffer, isPending: isPendingStatic } = useSubmitStaticOffer();

  const isSubmitting = isPendingPriceDiscovery || isPendingBenchmark || isPendingStatic;

  const initial = getScheduleFromTakerMarket(takerMarket);

  const [cadence, setCadence] = useState<Cadence>(initial.cadence);
  const [startDate, setStartDate] = useState<string | undefined>(initial.startDate);
  const [endDate, setEndDate] = useState<string | undefined>(initial.endDate);
  const [cadenceError, setCadenceError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      const { cadence, startDate, endDate } = getScheduleFromTakerMarket(takerMarket);
      setCadence(cadence);
      setStartDate(startDate);
      setEndDate(endDate);
      setCadenceError(null);
    }
  }, [open, takerMarket]);

  const isOngoingOffer = checkIfDurationIsATypicalOngoingOffer({ startDate, endDate, cadence });

  const handleSave = async () => {
    // Validate cadence for weekly schedules
    if (cadence.type === 'weekly' && cadence.value?.length === 0) {
      setCadenceError(t('scheduling.selectDayOrReset'));
      return;
    }

    setCadenceError(null);

    // Build schedule payload
    const schedule: ScheduleInput = isOngoingOffer
      ? null
      : {
          startDate,
          endDate,
          daysOfWeek: cadence.value,
        };

    try {
      if (takerMarket.marketPricingType === 'PRICE_DISCOVERY') {
        await submitPriceDiscoveryOffer({
          marketUuid: takerMarket.offerConfig.marketUuid,
          offerConfig: {
            uuid: takerMarket.offerConfig.uuid,
            isDiscountBidding: takerMarket.offerConfig.isDiscountBidding ?? false,
            maxDiscount: takerMarket.offerConfig.maxDiscount,
            maxApr: takerMarket.offerConfig.maxApr,
            exclusionSettings: takerMarket.offerConfig.exclusionSettings,
            expireOn: null,
            schedule,
            frequency: takerMarket.offerConfig.frequency,
          },
          supplierDivisionUuid: takerMarket.takerDivisionUuid,
          referrer: { workflow: 'OFFER_ACTIVITY' },
        });
      } else if (takerMarket.marketPricingType === 'BENCHMARK') {
        await submitBenchmarkOffer({
          marketUuid: takerMarket.offerConfig.marketUuid,
          supplierDivisionUuid: takerMarket.takerDivisionUuid,
          expireOn: null,
          schedule,
          frequency: takerMarket.offerConfig.frequency === 'ONE_TIME' ? 'one-time' : 'recurring',
          referrer: { workflow: 'OFFER_ACTIVITY' },
        });
      } else if (takerMarket.marketPricingType === 'STATIC') {
        await submitStaticOffer({
          rate: takerMarket.offerConfig.maxApr ?? 0,
          marketUuid: takerMarket.offerConfig.marketUuid,
          supplierDivisionUuid: takerMarket.takerDivisionUuid,
          expireOn: null,
          schedule,
          frequency: takerMarket.offerConfig.frequency === 'ONE_TIME' ? 'one-time' : 'recurring',
          referrer: { workflow: 'OFFER_ACTIVITY' },
        });
      } else {
        const error = new Error(`Unsupported market pricing type: ${takerMarket.marketPricingType}`);
        addError('Edit Schedule Modal - Invalid Market Pricing Type', {
          error,
          marketPricingType: takerMarket.marketPricingType,
          takerMarket: {
            marketUuid: takerMarket.marketUuid,
            takerDivisionUuid: takerMarket.takerDivisionUuid,
          },
        });
        showSnackbar({
          message: t('offerSubmitDialog.scheduleSetError'),
        });
        return;
      }

      trackEnterpriseEvent('edit-schedule-modal::schedule-saved', {
        marketUuid: takerMarket.marketUuid,
        isOngoingOffer,
        hasStartDate: !!startDate,
        hasEndDate: !!endDate,
        cadenceType: cadence.type,
      });

      showSnackbar({
        message: t('editScheduleModal.scheduleSaved'),
      });

      onClose();
    } catch (error) {
      const errorMessage = t('editScheduleModal.saveError');

      addError('Edit Schedule Modal Save Error', {
        error,
        takerMarket: {
          marketUuid: takerMarket.marketUuid,
          takerDivisionUuid: takerMarket.takerDivisionUuid,
        },
        schedule: {
          startDate,
          endDate,
          cadence,
        },
      });

      showSnackbar({
        message: errorMessage,
      });
    }
  };

  const scheduleDisplay = buildCustomScheduleSentenceFromFormData({
    startDate,
    endDate,
    cadence,
    t,
  });

  return (
    <>
      <ModalContent className="pt-1">
        {isOngoingOffer && (
          <div className="pb-2">
            <Alert
              variant="outlined"
              type="default"
              full
              title={t('editScheduleModal.ongoingRecurringOffer')}
              description={t('editScheduleModal.ongoingOfferDescription')}
            />
          </div>
        )}
        <div className="rounded-lg bg-blueGray-50">
          <div className="flex w-full flex-col gap-x-8 gap-y-3 border-b-[0.5px] border-b-stroke p-4 md:flex-row md:items-center md:justify-between">
            <div>{t('scheduling.schedule')}</div>
            <div className="font-medium md:text-right">{scheduleDisplay}</div>
          </div>
          <SchedulingInput
            takerMarkets={[takerMarket]}
            schedule={{ startDate, endDate, cadence }}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            setCadence={(newCadence) => {
              setCadence(newCadence);
              setCadenceError(null);
            }}
            errors={cadenceError ? { cadence: { message: cadenceError } } : {}}
          />
        </div>
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
          {t('core.cancel')}
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSubmitting} loading={isSubmitting}>
          {t('editScheduleModal.saveChanges')}
        </Button>
      </ModalActions>
    </>
  );
};

const EditScheduleModal = ({ open, takerMarket, onClose }: EditScheduleModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal open={open} onClose={onClose} className="sm:w-[750px]">
      <ModalTitleInverse>
        <span className="flex flex-col">
          <span>{t('editScheduleModal.editSchedule')}</span>
          <span className="text-sm font-normal text-text-secondary">{getTakerMarketName(takerMarket)}</span>
        </span>
      </ModalTitleInverse>
      <QueryBoundaries LoadingComponent={EditScheduleModalSkeleton}>
        <EditScheduleModalContent open={open} takerMarket={takerMarket} onClose={onClose} />
      </QueryBoundaries>
    </Modal>
  );
};

export default EditScheduleModal;
